import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { LogoutRequest } from 'src/app/pages/auth/login/models/logoutRequest';
import { of } from 'rxjs'; // Add this at the top
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  screenSize: string = '';
  mainURL = 'api/';
  private loginStatus$ = new BehaviorSubject<boolean>(this.hasValidTokens());
  private userInfo$ = new BehaviorSubject<any>(this.getUserInfo());

  constructor(private auth: AuthService, private http: HttpClient) {}

  // ------------------------------
  // Tokens & User Info
  // ------------------------------
  setUserInfo(user: any) {
    localStorage.setItem('userInfo', JSON.stringify(user));
    this.broadcastLoginStatus(true);
    this.userInfo$.next(user);
    this.broadcastUserInfoChange();
  }
  getUserInfo() {
    const info = localStorage.getItem('userInfo');
    return info ? JSON.parse(info) : null;
  }

  getUserInfo$(): Observable<any> {
    return this.userInfo$.asObservable();
  }
  getUserData() {
    return this.http.get(environment.apiUrl + this.mainURL + 'getUserProfile');
  }

  deleteUserInfo() {
    localStorage.removeItem('userInfo');
    this.broadcastLoginStatus(false);

    this.userInfo$.next(null);
    this.broadcastUserInfoChange();
  }

  setTokenInfo(
    accessToken: string,
    refreshToken: string,
    userId: string,
    role: string
  ) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('userId', userId);
    localStorage.setItem('role', typeof role === 'object' ? JSON.stringify(role) : role);
    this.broadcastLoginStatus(true);
  }

  setAccessToken(newToken: string) {
    localStorage.setItem('accessToken', newToken);
    this.broadcastLoginStatus(true);
  }

  deleteTokenInfo() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    this.broadcastLoginStatus(false);
  }

  getAcessToken() {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  getUserId() {
    return localStorage.getItem('userId');
  }
  updateUserDetails(user: any): Observable<any> {
    const formData = new FormData();

    if (user.email) formData.append('email', user.email);
    if (user.name) formData.append('name', user.name);
    if (user.country) formData.append('country', user.country);
    if (user.phoneNumber) formData.append('phoneNumber', user.phoneNumber);

    // Append picture file if provided
    if (user.picture) {
      formData.append('picture', user.picture);
    }

    // Get token from localStorage (adjust based on your token storage method)
    const token = localStorage.getItem('accessToken');

    // Set headers with authorization token
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    return this.http.patch(
      environment.apiUrl + this.mainURL + 'updateUserDetails',
      formData,
      {
        headers,
        responseType: 'text', // Expect plain text response instead of JSON
      }
    );
  }
  // ------------------------------
  // Login / Logout Helpers
  // ------------------------------
  isUserLogin(): boolean {
    return this.hasValidTokens();
  }

  private hasValidTokens(): boolean {
    return !!this.getAcessToken() && !!this.getRefreshToken();
  }

  getLoginStatus$(): Observable<boolean> {
    return this.loginStatus$.asObservable();
  }

  private broadcastLoginStatus(isLoggedIn: boolean) {
    this.loginStatus$.next(isLoggedIn);
    this.broadcastUserInfoChange();
  }

  private broadcastUserInfoChange() {
    window.dispatchEvent(
      new CustomEvent('userInfoChanged', {
        detail: this.getUserInfo(),
      })
    );
  }

  // ------------------------------
  // Google & Guest
  // ------------------------------
  async logInWithGoogleAccount(user: any) {
    this.auth.loginWithGoogle(user).subscribe(
      (response: any) => {
        this.setTokenInfo(
          response.accessToken,
          response.refreshToken,
          response.userId,
          response.role
        );

        const userInfo = {
          name: user.name || response.name,
          email: user.email || response.email,
          picture: user.picture || user.photoURL || response.picture,
          ...user,
          ...response.userInfo,
        };
        this.setUserInfo(userInfo);
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }, 100);
      },
      (error) => {
        console.error('Login failed:', error);
        setTimeout(() => window?.location.reload(), 3000);
      }
    );
  }

  logoutFromGoogleAccount() {
    const refreshToken = this.getRefreshToken();
    localStorage.clear();
    this.broadcastLoginStatus(false);
    this.userInfo$.next(null);

    if (refreshToken) {
      const logoutRequest: LogoutRequest = { refreshToken };
      return this.auth.logoutFromGoogle(logoutRequest);
    }
    return of(null);
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      const logoutRequest: LogoutRequest = { refreshToken };
      return this.auth.refreshAccessToken(logoutRequest);
    }
    return new BehaviorSubject(null);
  }

  // ------------------------------
  // UI Helpers
  // ------------------------------
  getUserImage() {
    const userInfo = this.getUserInfo();

    if (userInfo && !JSON.stringify(userInfo).includes('Guest User')) {
      let img =
        userInfo.picture ||
        userInfo.profileImage ||
        userInfo.avatar ||
        userInfo.photoURL ||
        '';

      if (img && img.startsWith('http')) {
        return img;
      }
      if (img) {
        return environment.apiImagePath + img;
      }
      return null;
    }

    return null;
  }

  getUserName(): string {
    const userInfo = this.getUserInfo();
    return userInfo && userInfo.name ? userInfo.name : '';
  }

  getUserInitials(): string {
    const name = this.getUserName();
    if (!name) return '';

    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  getScreenSize() {
    return this.screenSize;
  }

  setScreenSize(screenSize: string) {
    this.screenSize = screenSize;
  }

  collectUserInfo() {
    const userSource =
      typeof document !== 'undefined'
        ? document.referrer || 'Direct'
        : 'Direct';

    return {
      screenSize: this.getScreenSizeAsString(),
      operatingSystem: this.getOperatingSystem(),
      userSource,
    };
  }

  private getScreenSizeAsString(): string {
    let width = 0;
    let height = 0;

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      width =
        window.innerWidth ||
        document.documentElement?.clientWidth ||
        document.body?.clientWidth ||
        0;

      height =
        window.innerHeight ||
        document.documentElement?.clientHeight ||
        document.body?.clientHeight ||
        0;
    }

    return `${width} X ${height}`;
  }

  private getOperatingSystem(): string {
    let userAgent = '';
    if (typeof window !== 'undefined') {
      userAgent = window.navigator.userAgent;
    }

    switch (true) {
      case /Windows/.test(userAgent):
        return 'Windows';
      case /Mac/.test(userAgent):
        return 'Mac OS';
      case /iPhone|iPad/.test(userAgent):
        return 'iOS';
      case /Android/.test(userAgent):
        return 'Android';
      case /Linux/.test(userAgent):
        return 'Linux';
      case /CrOS/.test(userAgent):
        return 'Chrome OS';
      case /Windows Phone/.test(userAgent):
        return 'Windows Phone';
      default:
        return 'Unknown';
    }
  }
  deleteUserAccount() {
    const token = localStorage.getItem('accessToken');

    return this.http.delete(`${environment.apiUrl}${this.mainURL}deleteUser`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
